import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { IntlProvider as ReactIntlProvider, type IntlConfig } from 'react-intl';
import type { SupportedLocale, Messages } from './types';
import ptBR from './messages/pt-BR.json';
import enUS from './messages/en-US.json';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './index';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'opus-ia-lang';

/**
 * Static message bundles. PT-BR and en-US are bundled at build time.
 * Future locales can be loaded asynchronously here.
 */
const MESSAGE_BUNDLES: Record<SupportedLocale, Messages> = {
  'pt-BR': ptBR,
  'en-US': enUS,
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface LocaleContextValue {
  /** Current active locale */
  locale: SupportedLocale;
  /** Switch to a different locale (persists to localStorage + updates HTML lang) */
  setLocale: (locale: SupportedLocale) => void;
  /** List of all supported locales */
  supportedLocales: readonly SupportedLocale[];
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read persisted locale from localStorage.
 * Returns null if nothing stored or invalid value.
 */
function getStoredLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
    return stored as SupportedLocale;
  }
  return null;
}

/**
 * Detect the best locale from browser preferences.
 * Falls back to DEFAULT_LOCALE (pt-BR) if no match found.
 */
function detectBrowserLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;

  const browserLocales = navigator.languages ?? [navigator.language];

  for (const browserLocale of browserLocales) {
    const normalized = browserLocale.trim();

    // Exact match (e.g. "pt-BR")
    const exact = SUPPORTED_LOCALES.find((l) => l === normalized);
    if (exact) return exact;

    // Language-only match (e.g. "pt" -> "pt-BR")
    const langOnly = normalized.split('-')[0] ?? normalized;
    const partial = SUPPORTED_LOCALES.find((l) => l.startsWith(langOnly));
    if (partial) return partial;
  }

  return DEFAULT_LOCALE;
}

/**
 * Resolve initial locale with priority:
 * 1. Explicit defaultLocale prop (testing override)
 * 2. localStorage (user's explicit choice)
 * 3. Default locale (pt-BR) — opus-ia is a Brazilian product; default to PT-BR
 *    even when browser prefers other languages. User can switch via LanguageSwitcher.
 *
 * Browser detection is intentionally NOT in the chain because most users have
 * en-US as primary browser language and expect PT-BR UI.
 */
function resolveInitialLocale(defaultLocale?: SupportedLocale): SupportedLocale {
  if (defaultLocale) return defaultLocale;
  return getStoredLocale() ?? DEFAULT_LOCALE;
}

// detectBrowserLocale kept for reference / opt-in future use, but no longer wired up.
void detectBrowserLocale;

/**
 * Update the HTML document lang attribute to match the current locale.
 */
function syncHtmlLang(locale: SupportedLocale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface IntlProviderProps {
  children: ReactNode;
  /** Override detected locale (useful for testing) */
  defaultLocale?: SupportedLocale;
}

/**
 * Application IntlProvider.
 *
 * Wraps react-intl's IntlProvider and provides locale switching via context.
 *
 * Resolution order:
 * 1. Explicit `defaultLocale` prop (testing override)
 * 2. localStorage preference (user's previous choice)
 * 3. Browser language detection
 * 4. PT-BR fallback
 *
 * On locale change:
 * - Persists to localStorage under key "opus-ia-lang"
 * - Updates document.documentElement.lang
 * - Triggers re-render of all translated strings
 */
export function IntlProvider({ children, defaultLocale }: IntlProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    resolveInitialLocale(defaultLocale),
  );

  // Sync HTML lang attribute on mount and locale changes
  useEffect(() => {
    syncHtmlLang(locale);
  }, [locale]);

  const setLocale = useCallback((newLocale: SupportedLocale) => {
    if (!(SUPPORTED_LOCALES as readonly string[]).includes(newLocale)) return;
    setLocaleState(newLocale);
    localStorage.setItem(STORAGE_KEY, newLocale);
  }, []);

  const contextValue = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      supportedLocales: SUPPORTED_LOCALES,
    }),
    [locale, setLocale],
  );

  const messages = MESSAGE_BUNDLES[locale];

  // Suppress MISSING_TRANSLATION warnings: components like PageInfoTicker
  // intentionally probe optional keys with empty defaultMessage. We still
  // surface other intl errors (e.g. malformed ICU patterns).
  const handleIntlError = useCallback<NonNullable<IntlConfig['onError']>>((err) => {
    if (err.code === 'MISSING_TRANSLATION') return;
    // eslint-disable-next-line no-console
    console.error(err);
  }, []);

  return (
    <LocaleContext.Provider value={contextValue}>
      <ReactIntlProvider
        locale={locale}
        defaultLocale={DEFAULT_LOCALE}
        messages={messages}
        onError={handleIntlError}
      >
        {children}
      </ReactIntlProvider>
    </LocaleContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook to access locale state and switch function.
 *
 * Must be used within an IntlProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { locale, setLocale } = useLocale();
 *   return <button onClick={() => setLocale('en-US')}>{locale}</button>;
 * }
 * ```
 */
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within an IntlProvider');
  }
  return ctx;
}
